import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { LEGEND_ITEMS, CATEGORY_DEFINITIONS, type LegendItem } from '../../lib/graph/graphTheme';

interface LegendCategory {
  title: string;
  description: string;
  items: LegendItem[];
}

// Palette + category groupings now live in the shared graph theme so this
// legend, the React Flow renderer and the vis-network renderer stay in sync.
const ALL_LEGEND_ITEMS = LEGEND_ITEMS;

interface SchemaLegendProps {
  className?: string;
  defaultExpanded?: boolean;
  /** List of KG area names or module names actually used in the schema */
  usedAreas?: string[];
  /** Whether any node is marked as central */
  hasCentralNodes?: boolean;
}

export const SchemaLegend: React.FC<SchemaLegendProps> = ({
  className = '',
  defaultExpanded = false,
  usedAreas = [],
  hasCentralNodes = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Build dynamic legend based on what's actually used
  const dynamicCategories = useMemo(() => {
    // If no areas specified, show nothing (will prompt user)
    if (usedAreas.length === 0 && !hasCentralNodes) {
      return [];
    }

    const categories: LegendCategory[] = [];

    for (const categoryDef of CATEGORY_DEFINITIONS) {
      const matchingItems: LegendItem[] = [];

      for (const key of categoryDef.keys) {
        // Check if this key is used (case-insensitive match)
        const isUsed = usedAreas.some(
          area => area.toLowerCase() === key.toLowerCase() ||
                  area.toLowerCase().includes(key.toLowerCase()) ||
                  key.toLowerCase().includes(area.toLowerCase())
        );

        // Special handling for central nodes
        if (key === 'central' && hasCentralNodes) {
          matchingItems.push(ALL_LEGEND_ITEMS[key]);
        } else if (isUsed && ALL_LEGEND_ITEMS[key]) {
          matchingItems.push(ALL_LEGEND_ITEMS[key]);
        }
      }

      if (matchingItems.length > 0) {
        categories.push({
          title: categoryDef.title,
          description: categoryDef.description,
          items: matchingItems,
        });
      }
    }

    return categories;
  }, [usedAreas, hasCentralNodes]);

  // Don't render if nothing to show
  if (dynamicCategories.length === 0) {
    return null;
  }

  return (
    <div
      className={`bg-white border border-neutral-200 rounded-lg shadow-sm ${className}`}
      style={{ maxWidth: '260px' }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-neutral-500" />
          <span className="font-medium text-sm text-neutral-700">Color Legend</span>
          <span className="text-xs text-neutral-400">({usedAreas.length} types)</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-neutral-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-neutral-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 max-h-[350px] overflow-y-auto">
          {dynamicCategories.map(category => (
            <div key={category.title} className="space-y-1">
              {/* Category Header */}
              <div className="border-b border-neutral-100 pb-1">
                <h4 className="text-xs font-semibold text-neutral-700">
                  {category.title}
                </h4>
                <p className="text-[10px] text-neutral-400">
                  {category.description}
                </p>
              </div>

              {/* Category Items */}
              <div className="flex flex-wrap gap-2 pt-1">
                {category.items.map(item => (
                  <div
                    key={item.name}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px]"
                    style={{
                      backgroundColor: item.bg,
                      border: `1px solid ${item.border}`,
                      color: item.text,
                    }}
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: item.border,
                      }}
                    />
                    <span className="font-medium whitespace-nowrap">
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Relationship Legend */}
          <div className="pt-2 border-t border-neutral-100">
            <h4 className="text-xs font-semibold text-neutral-700 mb-2">
              Relationships
            </h4>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <svg width="24" height="8" className="flex-shrink-0">
                  <line x1="0" y1="4" x2="20" y2="4" stroke="#10b981" strokeWidth="2" />
                  <polygon points="20,4 16,1 16,7" fill="#10b981" />
                </svg>
                <span className="text-[10px] text-neutral-600">Semantic Link</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-neutral-500">
                <span className="font-mono bg-neutral-100 px-1.5 py-0.5 rounded text-[9px]">1:N</span>
                <span>One to Many</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-neutral-500">
                <span className="font-mono bg-neutral-100 px-1.5 py-0.5 rounded text-[9px]">N:N</span>
                <span>Many to Many</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemaLegend;
